import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { formatCurrency } from "@/lib/currency";
import { format, parseISO } from "date-fns";
import {
  CHART_TICK_FILL,
  CHART_GRID_STROKE,
  CHART_GRID_STROKE_OPACITY,
  chartTooltipContentStyle,
} from "@/lib/chartTheme";

interface RevenueChartProps {
  data: { period: string; revenue: number; transactionCount: number }[]; // Smallest currency unit (renamed from revenueCents)
}

export function RevenueChart({ data }: RevenueChartProps) {
  // Handle undefined or empty data
  if (!data || data.length === 0) {
    return (
      <Card className="col-span-2">
        <CardHeader>
          <CardTitle>Revenue Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No revenue data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Transform data: convert period to readable format
  // Backend returns revenue in major currency units, no conversion needed
  const chartData = data.map((item) => ({
    period: format(parseISO(item.period), "MMM yyyy"),
    revenue: item.revenue, // Already in major currency units
    transactions: item.transactionCount,
  }));

  return (
    <Card className="col-span-2">
      <CardHeader>
        <CardTitle>Revenue Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid
              stroke={CHART_GRID_STROKE}
              strokeDasharray="3 3"
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <XAxis
              dataKey="period"
              tick={{ fill: CHART_TICK_FILL }}
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <YAxis
              tick={{ fill: CHART_TICK_FILL }}
              stroke={CHART_GRID_STROKE}
              strokeOpacity={CHART_GRID_STROKE_OPACITY}
            />
            <Tooltip
              formatter={(value) => formatCurrency(value as number, "VND")}
              contentStyle={chartTooltipContentStyle}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="var(--chart-1)"
              strokeWidth={2}
              name="Revenue"
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
